import React, { useContext, useEffect } from 'react'
import { RouteComponentProps, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import qs from 'query-string'
import { selectRequestsAreLoading, selectCampaign } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    submitCampaign,
    updateCampaign,
    publishCampaign,
    setEditingCampaign,
    getCampaign,
    CAMPAIGN_STATUS_ENUM,
    CampaignState,
    updatePayoutDeadline,
    savePayoutDeadline,
} from 'state/campaigns'
import { useCheckPermissions, usePermission, useCheckSomePermissions } from 'hooks/permissions'
import { CampaignManagement, CampaignStatusControl } from 'components/Shared/Permission/enums'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import SaveForm from './SaveForm'
import { noop } from 'lodash'
import { selectCMUserUuid } from 'state/profile'
import { setTeamCountryByCampaign } from 'state/filter'
import Loading from 'components/Loading'

const Save: React.FC<{ uuid: string }> = ({ uuid }) => {
    const dispatch = useDispatch()
    const viewType = useContext(ViewTypeContext)
    const campaign = useSelector(selectCampaign(uuid))
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.Campaign]))
    const isSaving = useSelector(selectRequestsAreLoading([STATE_KEYS.SubmitCampaign]))
    const isPublishing = useSelector(selectRequestsAreLoading([STATE_KEYS.PublishCampaign]))
    const canPublishCampaign =
        (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    const campaignPermissionsCheck = useCheckPermissions([
        CampaignManagement.LiveCampaignEditDeadlines,
        CampaignManagement.LiveCampaignEditBriefs,
    ])
    useEffect(() => {
        if (uuid) {
            dispatch(
                getCampaign({
                    uuid,
                    onSuccess: campaign => {
                        dispatch(setTeamCountryByCampaign(campaign.uuid))
                    },
                }),
            )
            dispatch(setEditingCampaign({ uuid }))
        }
    }, [dispatch, uuid])

    useEffect(() => {
        return (): void => {
            dispatch(setEditingCampaign({ uuid: null }))
        }
    }, [dispatch])

    if (isLoading) return <Loading />
    if (!campaign) return null
    if (campaign?.in_template_flow) return <Redirect to="/404" />

    return (
        <SaveForm
            campaign={campaign}
            isSaving={canPublishCampaign ? isPublishing : isSaving}
            handleSubmit={(campaignData: CampaignState, isPublish: boolean): any => {
                if (viewType === EDITOR_ROUTE_ENUM.EDIT) {
                    if (!isPublish) {
                        const { payout_deadline, ...campaignDataWithoutPayoutDeadline } = campaignData
                        if (campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE) {
                            if (
                                campaignPermissionsCheck &&
                                campaignPermissionsCheck[CampaignManagement.LiveCampaignEditDeadlines] &&
                                !campaignPermissionsCheck[CampaignManagement.LiveCampaignEditBriefs]
                            ) {
                                if (payout_deadline && campaign.payout_deadline !== payout_deadline) {
                                    const payload = {
                                        showToast: true,
                                        payout_deadline,
                                        uuid: campaign.uuid,
                                    }
                                    dispatch(savePayoutDeadline(payload))
                                    dispatch(
                                        //update payout deadline
                                        updatePayoutDeadline(payload),
                                    )
                                }
                            } else if (
                                campaignPermissionsCheck &&
                                campaignPermissionsCheck[CampaignManagement.LiveCampaignEditBriefs]
                            ) {
                                return dispatch(
                                    //save changes
                                    updateCampaign({
                                        campaign: campaignDataWithoutPayoutDeadline,
                                        uuid: campaign.uuid,
                                        callback: () => {
                                            if (
                                                payout_deadline &&
                                                campaign.payout_deadline !== payout_deadline &&
                                                campaignPermissionsCheck &&
                                                campaignPermissionsCheck[CampaignManagement.LiveCampaignEditDeadlines]
                                            ) {
                                                const payload = {
                                                    payout_deadline,
                                                    uuid: campaign.uuid,
                                                }
                                                dispatch(savePayoutDeadline(payload))

                                                dispatch(
                                                    //update payout deadline
                                                    updatePayoutDeadline(payload),
                                                )
                                            }
                                        },
                                    }),
                                )
                            }
                        } else {
                            return dispatch(
                                //save changes
                                updateCampaign({
                                    campaign: campaignData,
                                    uuid: campaign.uuid,
                                }),
                            )
                        }
                    } else if (canPublishCampaign) {
                        return dispatch(
                            //launch campaign
                            publishCampaign({
                                data: {
                                    campaign: campaignData,
                                    uuid: campaign.uuid,
                                },
                            }),
                        )
                    } else {
                        return dispatch(
                            //submit to pending
                            submitCampaign({
                                data: {
                                    campaign: campaignData,
                                    uuid: campaign.uuid,
                                },
                            }),
                        )
                    }
                } else {
                    return noop
                }
            }}
        />
    )
}

const Wrapper: React.FC<RouteComponentProps> = ({ location }) => {
    const { campaign: uuid } = qs.parse(location.search)
    //this is to check if profile is loaded
    const profileUuid = useSelector(selectCMUserUuid)
    const hasCreateNewCampaignPermission = useCheckSomePermissions([
        CampaignManagement.CreateAndEditBriefsForInternal,
        CampaignManagement.CreateAndEditBriefsForExternal,
    ])

    if (profileUuid && !hasCreateNewCampaignPermission && !uuid) return <Redirect to="/403" />

    if (typeof uuid !== 'string') return null
    return <Save uuid={uuid} />
}

export default Wrapper
